import { cn } from '@virginexperiencedays/tailwind';
import type { CSSProperties, HTMLAttributes } from 'react';
import { BrandAccentHorizontalSvg } from './BrandAccentHorizontalSvg';
import { BrandAccentVerticalSvg } from './BrandAccentVerticalSvg';

type Props = {
  fillerWidth?: {
    mobile?: number;
    tablet?: number;
    desktop?: number;
  };
  isHorizontalOnMobile?: boolean;
} & HTMLAttributes<HTMLDivElement>;

/**
 * BrandAccentOverlay is a decorative component that renders a brand accent overlay
 * with a flexible SVG cutout shape and pseudo-elements to match design requirements.
 *
 * The overlay can be displayed in either a vertical or horizontal orientation, adapting
 * responsively based on the `isHorizontalOnMobile` prop. The component uses a "filler"
 * element to ensure the overlay matches the container size, making it reusable across
 * different layouts and components.
 *
 * @param className - Additional class names to apply to the root element.
 * @param fillerWidth - An object specifying the width of the filler element for desktop, tablet and mobile layouts.
 * @param isHorizontalOnMobile - If true, displays the horizontal version on mobile and vertical on desktop.
 * @param rest - Additional props spread to the root div.
 *
 * @remarks
 * - The SVGs and pseudo-elements are purely decorative and do not require accessibility titles.
 * - The overlay is positioned absolutely and is pointer-events-none, so it does not interfere with user interaction.
 * - The component is designed to be used within a parent that has a relative position to ensure the overlay is positioned correctly.
 * - Depending on the parent's layout, the parent might need to have z-index set to ensure the overlay doesn't overlap other content.
 */
export const BrandAccentOverlay = ({
  className,
  fillerWidth,
  isHorizontalOnMobile,
  ...rest
}: Props) => {
  return (
    <div
      className={cn(
        'absolute inset-0 hidden text-current z-0 pointer-events-none overflow-hidden rebrand:flex',
        isHorizontalOnMobile && 'flex-col-reverse lg:flex-row',
        className,
      )}
      aria-hidden="true"
      {...rest}
    >
      {/* filler element to adjust to container size vs actual svg width in designs as they differ so the component is reuseable */}
      <div
        className={cn(
          'relative w-[var(--filler-w-mobile)] h-full bg-current',
          'md:w-[var(--filler-w-tablet)]',
          'lg:w-[var(--filler-w-desktop)] lg:h-full',
          // fix for safari - before/afters cover small gaps between the elements that would appear on some screen sizes
          'before:absolute before:top-0 before:left-[-1px] before:w-0.5 before:h-full before:bg-current before:z-[-1]',
          'after:absolute after:top-0 after:left-full after:w-0.5 after:h-full after:bg-current after:z-[-1]',
          isHorizontalOnMobile && [
            'w-full h-[var(--filler-w-mobile)] md:w-full md:h-[var(--filler-w-tablet)]',
            'before:top-[-1px] before:left-0 before:w-full before:h-0.5 md:before:top-0 md:before:left-[-1px] md:before:w-0.5 md:before:h-full',
            'after:top-full after:left-0 after:w-full after:h-0.5 md:after:top-0 md:after:left-full md:after:w-0.5 md:after:h-full',
          ],
        )}
        style={
          {
            '--filler-w-desktop': `${fillerWidth?.desktop ?? 0}px`,
            '--filler-w-mobile': `${fillerWidth?.mobile ?? 0}px`,
            '--filler-w-tablet': `${fillerWidth?.tablet ?? 0}px`,
          } as CSSProperties
        }
      />
      {/*
        svg cut out shape + pseudoelements to have fixed svg size for ratio as in design and only
        extend 'the arms' (pseudoelements) to container height
        this is to make it adjust to any container height and not having to use different svgs for each component
      */}
      {/* vertical version */}
      <div
        className={cn(
          'relative flex items-center w-full h-full',
          'before:absolute before:top-[calc(-50%_-_2px)] before:left-[3.8px] before:w-1/2 before:h-full before:bg-current before:origin-bottom-right before:transform before:translate-x-[-100%] before:rotate-[30.4deg]',
          'after:absolute after:top-[calc(50%_+_2px)] after:left-[3.8px] after:w-1/2 after:h-full after:bg-current after:origin-top-right after:transform after:translate-x-[-100%] after:rotate-[-30.4deg]',
          isHorizontalOnMobile && 'hidden lg:flex',
        )}
      >
        <BrandAccentVerticalSvg />
      </div>

      {/* horizontal mobile version */}
      {isHorizontalOnMobile && (
        <div
          className={cn(
            'relative hidden items-end justify-center w-full h-full',
            'before:absolute before:bottom-[2.7px] before:left-[-2px] before:w-1/2 before:h-[150%] before:bg-current before:origin-bottom-right before:transform before:rotate-[-68.7deg]',
            'after:absolute after:bottom-[2.7px] after:left-[calc(50%_+_2px)] after:w-1/2 after:h-[150%] after:bg-current after:origin-bottom-left after:transform after:rotate-[68.7deg]',
            isHorizontalOnMobile && 'flex lg:hidden',
          )}
        >
          <BrandAccentHorizontalSvg />
        </div>
      )}
    </div>
  );
};
